import { Link, Typography } from '@material-ui/core';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  Column,
  Columns,
  DrawerActions,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import {
  Box,
  Button,
  DescriptionLineItem,
  TextBox,
} from '@superdispatch/ui-lab';
import { useQueryClient } from '@tanstack/react-query';
import { useCreditCard } from 'shared/errors/PaymentService';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { SlowFormikDrawer } from 'shared/form/SlowFormikDrawer';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { EncryptIcon } from 'shared/icons/EncryptIcon';
import {
  PastDueInvoice,
  getPastDueTotal,
} from 'shared/modules/subscription/core/PastDueInvoice';
import { SubscriptionBillingForm } from 'shared/modules/subscription/core/SubscriptionBillingForm';
import { SubscriptionChargebeeCard } from 'shared/modules/subscription/core/SubscriptionChargebeeCard';
import { billingAddressFormErrorsGetter } from 'shared/modules/subscription/core/SubscriptionUtils';
import { trackSubscriptionEvent } from 'shared/modules/subscription/data/SubscriptionAnalytics';
import {
  useBillingAddress,
  useEstimateTaxPrice,
  useInvalidateSubscriptionQueries,
  useSubscription,
  useSubscriptionAPI,
} from 'shared/modules/subscription/SubscriptionAPI';
import {
  subscriptionContactSchema,
  subscriptionSchema,
} from 'shared/modules/subscription/SubscriptionDTO';
import { useFlag } from 'shared/settings/FeatureToggles';
import { BackButton } from 'shared/ui/BackButton';
import { ChargebeeEmptyPlaceholder } from 'shared/ui/ChargebeeEmptyPlaceholder';
import styled from 'styled-components';
import { z } from 'zod';
import { SubscriptionContactFields } from 'shared/modules/subscription/core/SubscriptionContactFields';

const validationSchema = toFormikValidationSchema(subscriptionSchema);
const validationSchemaWithContact = toFormikValidationSchema(
  z.intersection(subscriptionSchema, subscriptionContactSchema),
);

export interface PastDueCardDetailsLocationState {
  isFromPastDueDrawer?: boolean;
}

interface SubscriptionPastDueCardDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
}

const StyledSlowFormikDrawer = styled(SlowFormikDrawer)`
  .MuiDrawer-paper {
    width: 100%;
    max-width: 432px;
  }
`;

// `DrawerActions` hides its top border once the marker right after it is
// visible. A fractional content height can leave that marker just below the
// scroll edge, so the border never disappears without this spacer.
const ScrollEndSpacer = styled.div`
  height: 1px;
`;

export function SubscriptionPastDueCardDetailsDrawer({
  open,
  onClose,
  onBack,
}: SubscriptionPastDueCardDetailsDrawerProps) {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbarStack();
  const { subscription: subscriptionDetails, billableSeats } =
    useSubscription();
  const { data: billingAddress } = useBillingAddress();
  const { data: taxPrice } = useEstimateTaxPrice(
    {
      seats_count: billableSeats?.billable_seats,
      include_total_invoice: false,
    },
    { enabled: open && !!billableSeats?.billable_seats },
  );
  const {
    saveBillingAddress,
    updateSubscriptionContact,
    updateSubscriptionSettings,
    collectPaymentPastDue,
  } = useSubscriptionAPI();
  const invalidateSubscriptionQueries = useInvalidateSubscriptionQueries();
  const { addCreditCard, cardRef, isChargebeeAvailable } = useCreditCard(
    subscriptionDetails?.client_token || '',
  );
  const isSubscriptionContactEnabled = useFlag('ctms_subscription_contact');

  const subscriptionContact = subscriptionDetails?.subscription?.contact;
  const pastDueInvoices = taxPrice?.past_due_invoices || [];
  const totalAmountDue = getPastDueTotal(pastDueInvoices);

  const formik = useFormikEnhanced({
    key: open,
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema: isSubscriptionContactEnabled
      ? validationSchemaWithContact
      : validationSchema,
    initialValues: {
      country: billingAddress?.billing_country || 'US',
      state: billingAddress?.billing_state || '',
      zip: billingAddress?.billing_zip || '',
      city: billingAddress?.billing_city || '',
      address: billingAddress?.billing_address || '',
      contact_first_name: subscriptionContact?.first_name || '',
      contact_last_name: subscriptionContact?.last_name || '',
      contact_email: subscriptionContact?.email || '',
    },
    getFormErrors: (error) => {
      return billingAddressFormErrorsGetter(error);
    },
    onSubmit: async (values) => {
      await saveBillingAddress({
        billing_country: values.country,
        billing_zip: values.zip,
        billing_state: values.state,
        billing_city: values.city,
        billing_address: values.address,
      });

      const isContactChanged =
        values.contact_first_name.trim() !== subscriptionContact?.first_name ||
        values.contact_last_name.trim() !== subscriptionContact?.last_name ||
        values.contact_email.trim() !== subscriptionContact?.email;

      if (isSubscriptionContactEnabled && isContactChanged) {
        await updateSubscriptionContact({
          first_name: values.contact_first_name.trim(),
          last_name: values.contact_last_name.trim(),
          email: values.contact_email.trim(),
        });
      }

      const { token, vaultToken } = await addCreditCard({});
      await updateSubscriptionSettings({
        payment_method_nonce: token,
        id: token,
        id_at_vault: vaultToken,
      });

      return Promise.all(
        pastDueInvoices.map((invoice) => collectPaymentPastDue(invoice.guid)),
      );
    },
    onSubmitSuccess: () => {
      invalidateSubscriptionQueries();
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      trackSubscriptionEvent({
        name: 'Carrier Edited Subscription Payment Information',
      });
      addSnackbar('Past due balance was paid', { variant: 'success' });
      onClose();
    },
    onSubmitFailure: (error) => {
      addSnackbar(error.message, { variant: 'error' });
    },
  });

  return (
    <StyledSlowFormikDrawer
      formik={formik}
      open={open}
      onClose={onClose}
      title={
        onBack ? (
          <Inline verticalAlign="center">
            <BackButton onClick={onBack} />
            Edit Card Details
          </Inline>
        ) : (
          'Edit Card Details'
        )
      }
      actions={
        <>
          <DrawerActions>
            <Box width="100%" paddingTop="small" paddingBottom="small">
              <Stack space="medium">
                <PastDueInvoice pastDueInvoices={pastDueInvoices} />

                <DescriptionLineItem
                  title={<TextBox variant="heading-4">Total</TextBox>}
                >
                  <TextBox variant="heading-4" align="right">
                    {formatCurrency(totalAmountDue)}
                  </TextBox>
                </DescriptionLineItem>

                <Stack space="small" align="center">
                  <Button
                    type="submit"
                    size="large"
                    fullWidth={true}
                    pending={formik.isSubmitting}
                    disabled={!pastDueInvoices.length}
                  >
                    Save & Pay {formatCurrency(totalAmountDue)}
                  </Button>

                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://support.superdispatch.com/hc/articles/35648690974739"
                  >
                    Refund Policy
                  </Link>
                </Stack>
              </Stack>
            </Box>
          </DrawerActions>
          <ScrollEndSpacer />
        </>
      }
    >
      <Stack space="medium">
        <Columns space="xxsmall" align="center">
          <Column width="content">
            <EncryptIcon />
          </Column>
          <Column width="fluid">
            <TextBox variant="caption" color="secondary">
              Encrypted. We don&apos;t store your card details on our servers.
            </TextBox>
          </Column>
        </Columns>

        {isChargebeeAvailable ? (
          <SubscriptionChargebeeCard hasCardHolderName={false} ref={cardRef} />
        ) : (
          <ChargebeeEmptyPlaceholder />
        )}

        {isSubscriptionContactEnabled && (
          <Stack space="xxsmall">
            <Typography variant="h4">Subscription Contact</Typography>
            <SubscriptionContactFields />
          </Stack>
        )}

        <Stack space="small">
          <Typography variant="h4">Billing Address</Typography>
          <SubscriptionBillingForm />
        </Stack>
      </Stack>
    </StyledSlowFormikDrawer>
  );
}
